/**
 * The live-view connection for one job: opens an audited view session,
 * long-polls for frames, paints them, and closes the session.
 *
 * State machine (what the panel shows):
 *
 *   idle        not watching
 *   connecting  session being opened, or open and no frame yet
 *   live        frames are arriving
 *   withheld    the runner is deliberately not sending the screen
 *               (a sign-in credential is readable on it)
 *   offline     the session is open but no runner is connected
 *   retrying    the relay or the network failed; trying again
 *   ended       the session is over (expired, job finished, or closed)
 *   denied      the API refused (role)
 *   error       could not start
 *
 * Frames never enter React state or any cache: each one is painted to
 * the canvas and dropped. Only its sequence number and age are kept.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "@/lib/apiClient";

import { clearCanvas, drawFrame } from "./frameCanvas";
import {
  closeLiveSession,
  fetchLiveFrame,
  openLiveSession,
  type RunnerMode,
} from "./liveApi";

export type LivePhase =
  | "idle"
  | "connecting"
  | "live"
  | "withheld"
  | "offline"
  | "retrying"
  | "ended"
  | "denied"
  | "error";

export type EndReason = "closed" | "expired" | "job_finished" | "not_running" | "unavailable";

export interface LiveView {
  phase: LivePhase;
  sessionId: string | null;
  /** Why the view ended or could not start. */
  endReason: EndReason | null;
  /** Reference for support, from the failed response. */
  correlationId: string | null;
  /** Age of the frame on screen when it was received, in ms. */
  frameAgeMs: number | null;
  mode: RunnerMode | null;
  start: () => void;
  stop: () => void;
}

const RETRY_MS = 2_000;
const RATE_LIMIT_MS = 5_000;

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener("abort", () => {
      clearTimeout(timer);
      resolve();
    });
  });
}

function status(error: unknown): number | null {
  return error instanceof ApiError ? error.status : null;
}

export function useLiveView(
  jobId: string,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
): LiveView {
  const [phase, setPhase] = useState<LivePhase>("idle");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [endReason, setEndReason] = useState<EndReason | null>(null);
  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const [frameAgeMs, setFrameAgeMs] = useState<number | null>(null);
  const [mode, setMode] = useState<RunnerMode | null>(null);
  // The running connection. Aborting it ends the loop and closes the session.
  const connection = useRef<AbortController | null>(null);

  const finish = useCallback(
    (next: LivePhase, reason: EndReason | null) => {
      // The last frame must not stay on screen after the view ends.
      clearCanvas(canvasRef.current);
      setPhase(next);
      setEndReason(reason);
      setSessionId(null);
      setFrameAgeMs(null);
      setMode(null);
    },
    [canvasRef],
  );

  const stop = useCallback(() => {
    connection.current?.abort();
    connection.current = null;
  }, []);

  const start = useCallback(() => {
    if (connection.current) return;
    const controller = new AbortController();
    connection.current = controller;
    const { signal } = controller;
    setPhase("connecting");
    setEndReason(null);
    setCorrelationId(null);

    void (async () => {
      let session: string | null = null;
      let outcome: [LivePhase, EndReason | null] = ["ended", "closed"];
      try {
        try {
          session = (await openLiveSession(jobId)).sessionId;
        } catch (error) {
          setCorrelationId(error instanceof ApiError ? error.correlationId : null);
          const code = status(error);
          if (code === 403) outcome = ["denied", null];
          else if (code === 409) outcome = ["ended", "not_running"];
          else if (code === 503) outcome = ["error", "unavailable"];
          else if (code === 404) outcome = ["ended", "job_finished"];
          else outcome = ["error", null];
          return;
        }
        if (signal.aborted) return;
        setSessionId(session);

        let after = 0;
        while (!signal.aborted) {
          try {
            const response = await fetchLiveFrame(jobId, session, after, signal);
            if (signal.aborted) break;
            setMode(response.mode);
            if (response.frame) {
              after = response.frame.seq;
              const canvas = canvasRef.current;
              if (canvas) await drawFrame(canvas, response.frame.jpegBase64);
              setFrameAgeMs(response.frame.ageMs);
              setPhase("live");
            } else if (response.mode === "withheld") {
              clearCanvas(canvasRef.current);
              setPhase("withheld");
            } else if (!response.runnerConnected) {
              setPhase("offline");
            } else {
              // Connected, nothing new. Either the page is not changing
              // (stay live) or no frame has arrived yet (keep waiting).
              setPhase(after > 0 ? "live" : "connecting");
            }
          } catch (error) {
            if (signal.aborted) break;
            const code = status(error);
            if (code === 404) {
              // The session is gone: expired, or the job is over.
              outcome = ["ended", "expired"];
              session = null; // nothing left to close
              return;
            }
            if (code === 403) {
              outcome = ["denied", null];
              return;
            }
            setCorrelationId(error instanceof ApiError ? error.correlationId : null);
            setPhase("retrying");
            await wait(code === 429 ? RATE_LIMIT_MS : RETRY_MS, signal);
          }
        }
      } finally {
        if (connection.current === controller) connection.current = null;
        if (session) {
          // Audited close. Best effort: an expired session closes itself.
          const closing = session;
          void Promise.resolve()
            .then(() => closeLiveSession(jobId, closing))
            .catch(() => undefined);
        }
        finish(outcome[0], outcome[1]);
      }
    })();
  }, [canvasRef, finish, jobId]);

  // Leaving the screen closes the session.
  useEffect(() => stop, [stop]);

  return { phase, sessionId, endReason, correlationId, frameAgeMs, mode, start, stop };
}
